/**
 * @fileoverview Slice model + small-slice aggregation for the product
 * breakdown donuts. Merging the sub-threshold tail into "Others" guarantees
 * donut leader labels can never cluster into unreadable overlaps.
 * @module features/home/utils/breakdownSlices
 */

import type { ReactNode } from 'react'

/** Slices below this share are merged into "Others". */
export const MIN_LABELED_SHARE = 0.05
/** Hard cap on labeled slices before the rest goes to "Others". */
export const MAX_MAJOR_SLICES = 5

export interface BreakdownSlice {
  key: string
  /** Slice label rendered next to the donut (emoji flags render fine in SVG). */
  name: string
  /** Name without the glyph prefix, for the single-source state. */
  plainName: string
  /** Large glyph for the single-source state (flag emoji / platform icon). */
  glyphLarge: ReactNode
  /**
   * Small SVG icon rendered at the label's leader-line end. Only for entries
   * whose symbol cannot be embedded in SVG text (brand icons); country flags
   * are emoji and travel inside `name` instead.
   */
  labelIcon?: ReactNode
  value: number
  fill: string
  /** For the synthetic "Others" slice: the merged-away slices (hover detail). */
  constituents?: BreakdownSlice[]
  [extra: string]: unknown
}

/**
 * Merges sub-threshold slices into one "Others" slice so donut labels cannot
 * overlap. A single small slice is kept as-is — it has nothing to collide with.
 */
export function aggregateSlices(
  slices: BreakdownSlice[],
  others: { name: string; fill: string }
): BreakdownSlice[] {
  const total = slices.reduce((sum, s) => sum + s.value, 0)
  const major: BreakdownSlice[] = []
  const minor: BreakdownSlice[] = []
  for (const slice of slices) {
    const share = total > 0 ? slice.value / total : 0
    if (share >= MIN_LABELED_SHARE && major.length < MAX_MAJOR_SLICES) major.push(slice)
    else minor.push(slice)
  }
  if (minor.length <= 1) return [...major, ...minor]
  return [
    ...major,
    {
      key: '__others__',
      name: others.name,
      plainName: others.name,
      glyphLarge: null,
      value: minor.reduce((sum, s) => sum + s.value, 0),
      fill: others.fill,
      constituents: minor,
    },
  ]
}
